import { useState } from "react";
import {
  LOG_FIELDS,
  LOG_FIELD_LABEL,
  fillDraftFromSim,
  logCompleteness,
  logDraftError,
  nextLogField,
  simTimesAvailable,
  type LogDraft,
  type LogField,
  type SimTimes,
} from "../logDraft";

export function LogForm({
  draft,
  sim,
  onChange,
}: {
  draft: LogDraft;
  sim: SimTimes;
  onChange: (next: LogDraft) => void;
}) {
  const [picker, setPicker] = useState<LogField | null>(null);
  const [hour, setHour] = useState("12");
  const [minute, setMinute] = useState("00");
  const dot = logCompleteness(draft);
  const error = logDraftError(draft);

  function open(field: LogField) {
    const existing = draft[field] ? new Date(draft[field]) : new Date();
    if (!Number.isNaN(existing.getTime())) {
      setHour(String(existing.getUTCHours()).padStart(2, "0"));
      setMinute(String(existing.getUTCMinutes()).padStart(2, "0"));
    }
    setPicker(field);
  }

  function commit(field: LogField, advance: boolean) {
    const stamp = new Date();
    stamp.setUTCHours(Number(hour), Number(minute), 0, 0);
    const next = { ...draft, [field]: stamp.toISOString(), updatedAt: new Date().toISOString(), confirmed: false };
    onChange(next);
    const following = advance ? nextLogField(field) : null;
    if (following) open(following);
    else setPicker(null);
  }

  return (
    <div data-testid="log-form">
      <p className="kicker">
        Journey log <span className={`legality-dot is-${dot === "complete" ? "ok" : dot === "invalid" ? "alert" : "warn"}`} data-testid="log-dot" />
      </p>
      <ul className="pilot-list">
        {LOG_FIELDS.map((field) => (
          <li key={field}>
            <button type="button" className="log-time" data-testid={`log-${field}`} onClick={() => open(field)}>
              <span>{LOG_FIELD_LABEL[field]}</span>
              <strong>{draft[field] ? `${draft[field].slice(11, 16)}Z` : "Set"}</strong>
            </button>
          </li>
        ))}
      </ul>
      {error && dot !== "empty" ? <p className="err">{error}</p> : null}
      <div className="row">
        {simTimesAvailable(sim) ? (
          <button
            type="button"
            className="btn ghost"
            data-testid="fill-from-sim"
            onClick={() => onChange(fillDraftFromSim(draft, sim))}
          >
            Fill from sim
          </button>
        ) : null}
        <label className="confirm-tick">
          <input
            type="checkbox"
            data-testid="captain-confirm"
            checked={draft.confirmed}
            disabled={dot !== "complete"}
            onChange={(event) => onChange({ ...draft, confirmed: event.target.checked, updatedAt: new Date().toISOString() })}
          />
          Captain confirms these times
        </label>
      </div>
      {picker ? (
        <div className="efb-scrim" data-testid="time-picker" onClick={() => setPicker(null)}>
          <section className="efb-sheet" role="dialog" aria-label={LOG_FIELD_LABEL[picker]} onClick={(event) => event.stopPropagation()}>
            <header className="efb-sheet-head">
              <strong>{LOG_FIELD_LABEL[picker]}</strong>
              <button type="button" className="efb-text-btn" onClick={() => setPicker(null)}>
                Cancel
              </button>
            </header>
            <div className="row">
              <label className="field">
                Hour
                <input value={hour} inputMode="numeric" onChange={(event) => setHour(event.target.value.replace(/\D/g, "").slice(0, 2))} />
              </label>
              <label className="field">
                Minute
                <input value={minute} inputMode="numeric" onChange={(event) => setMinute(event.target.value.replace(/\D/g, "").slice(0, 2))} />
              </label>
            </div>
            <div className="row">
              <button type="button" className="btn" data-testid="time-confirm" onClick={() => commit(picker, false)}>
                Confirm
              </button>
              {nextLogField(picker) ? (
                <button type="button" className="btn ghost" data-testid="time-next" onClick={() => commit(picker, true)}>
                  Next
                </button>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
